import * as React from "react";
import {
  getTenantId as readGlobalTenantId,
  setTenantId as setGlobalTenantId,
} from "@shared/lib/auth";
import {
  clearStoredTenant,
  loadStoredTenant,
  saveStoredTenant,
  TENANT_CHANGED_EVENT,
} from "@shared/lib/tenantStorage";
import { translateUi } from "@i18n/config";

type TenantContextValue = {
  tenantId: string | null;
  setTenantId: (tenantId: string | null) => void;
  setTenantWithRedirect: (id: string, domain?: string | null) => void;
};

function loadTenant(): string | null {
  if (typeof window === "undefined") return readGlobalTenantId() ?? null;
  try {
    const v = loadStoredTenant();
    const id = v || readGlobalTenantId() || null;
    if (id) setGlobalTenantId(id);
    return id;
  } catch {
    return readGlobalTenantId() || null;
  }
}

function persistTenant(id: string | null) {
  if (typeof window === "undefined") return;
  saveStoredTenant(id);
}

function dispatchTenantChanged(id: string | null) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(TENANT_CHANGED_EVENT, { detail: { tenantId: id } }),
    );
  } catch {
    // ignore event dispatch failures
  }
}

const TenantContext = React.createContext<TenantContextValue | undefined>(
  undefined,
);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenantId, setTenantIdState] = React.useState<string | null>(
    loadTenant(),
  );

  const setTenantId = React.useCallback((id: string | null) => {
    setTenantIdState(id);
    persistTenant(id);
    setGlobalTenantId(id);
  }, []);

  const setTenantWithRedirect = React.useCallback(
    (id: string, domain?: string | null) => {
      const currentHost = window.location.hostname;
      // If domain is provided and different from current host, perform hard redirect
      if (domain && domain !== currentHost) {
        persistTenant(id);
        setGlobalTenantId(id);
        // Construct new URL. Assuming /admin is the base path for admin app.
        // We also preserve the current path if possible, or just go to /admin.
        const protocol = window.location.protocol;
        window.location.href = `${protocol}//${domain}/admin?tenant=${id}`;
        return;
      }
      setTenantId(id);
    },
    [setTenantId],
  );

  // Sync initial/current state to auth module
  React.useEffect(() => {
    setGlobalTenantId(tenantId);
    dispatchTenantChanged(tenantId);
  }, [tenantId]);

  React.useEffect(() => {
    function clearTenant() {
      setTenantIdState(null);
      clearStoredTenant();
      setGlobalTenantId(null);
    }

    if (typeof window !== "undefined") {
      window.addEventListener("auth:session-cleared", clearTenant);
      return () =>
        window.removeEventListener("auth:session-cleared", clearTenant);
    }
  }, []);

  const value = React.useMemo(
    () => ({ tenantId, setTenantId, setTenantWithRedirect }),
    [tenantId, setTenantId, setTenantWithRedirect],
  );
  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = React.useContext(TenantContext);
  if (!ctx)
    throw new Error(
      translateUi(
        "useTenantMustBeUsedWithinTenantProvider",
        "useTenant must be used within TenantProvider",
      ),
    );
  return ctx;
}
